import { useCallback, useMemo } from "react";
import InputText from "../../staff/InputText";
import InputTextArea from "../../staff/InputTextArea";
import { usePersonForm } from "./personForm/PersonFormContext";

const YEAR_JOINED_MIN = 1990;

interface BioProps {
  bioRequired?: boolean;
}

const Bio = ({ bioRequired = false }: BioProps) => {
  const { form, setField, touchField, getFieldError, shouldShowError } = usePersonForm();

  const yearJoinedMax = useMemo(() => new Date().getFullYear(), []);

  const yearJoinedDisplay =
    form.yearJoinedFirm == null ? "" : String(form.yearJoinedFirm);

  const handleYearJoinedChange = useCallback(
    (raw: string) => {
      if (raw.trim() === "") {
        setField("yearJoinedFirm", undefined);
        return;
      }
      const digits = raw.replace(/\D/g, "").slice(0, 4);
      if (digits === "") {
        setField("yearJoinedFirm", undefined);
        return;
      }
      setField("yearJoinedFirm", parseInt(digits, 10));
    },
    [setField]
  );

  const handleYearJoinedBlur = useCallback(() => {
    const y = form.yearJoinedFirm;
    if (y == null || !Number.isFinite(y)) {
      touchField("yearJoinedFirm");
      return;
    }
    const clamped = Math.min(
      yearJoinedMax,
      Math.max(YEAR_JOINED_MIN, y)
    );
    if (clamped !== y) {
      setField("yearJoinedFirm", clamped);
    }
    touchField("yearJoinedFirm");
  }, [
    form.yearJoinedFirm,
    setField,
    touchField,
    yearJoinedMax,
  ]);

  return (
    <div className="flex flex-col gap-5">
      <InputTextArea
        title="Summary"
        placeholder="ex: I am a lawyer with 10 years of experience in the field of law."
        value={form.bio ?? ""}
        onChange={e => setField("bio", e)}
        maxChars={4000}
        showCharacterCount
        minHeightClassName="min-h-[200px]"
        onBlur={() => touchField("bio")}
        required={bioRequired}
        error={shouldShowError("bio") && Boolean(getFieldError("bio"))}
        errorMessage={getFieldError("bio")}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      <InputText
        title="Birth Information"
        value={form.birthInformation ?? ""}
        onChange={(v) => setField("birthInformation", v)}
        onBlur={() => touchField("birthInformation")}
      />
      <InputText
        title="ISLN"
        value={form.isln ?? ""}
        onChange={(v) => setField("isln", v)}
        onBlur={() => touchField("isln")}
      />
      <InputText
        title="Year Joined the Law Firm"
        type="number"
        min={YEAR_JOINED_MIN}
        max={yearJoinedMax}
        value={yearJoinedDisplay}
        onChange={handleYearJoinedChange}
        onBlur={handleYearJoinedBlur}
        error={shouldShowError("yearJoinedFirm") && Boolean(getFieldError("yearJoinedFirm"))}
        errorMessage={getFieldError("yearJoinedFirm")}
      />
      </div>
    </div>
  );
};

export default Bio;
